import { CODE_SYSTEMS, LOINC_CODES } from '@liza/fhir-utils';
import type { PlanDefinition, PlanDefinitionAction, Timing } from '@medplum/fhirtypes';
import { every, itemKind, loincCode, patient, taskCategory } from './builders';

/** A guideline a citation on this protocol points at: the exact title to print and the URL to link. */
interface ThyroidGuideline {
  readonly title: string;
  readonly url: string;
}

const AACE_ATA_HYPOTHYROIDISM_2012: ThyroidGuideline = {
  title: 'AACE/ATA Clinical Practice Guidelines for Hypothyroidism in Adults (2012)',
  url: 'https://www.aace.com/disease-and-conditions/thyroid',
};

/**
 * ATA's recommendation numbers could not be verified against primary text — the publisher refuses
 * automated access — so a citation against this source names the section it sits in rather than
 * asserting a number that may not exist.
 */
const ATA_HYPERTHYROIDISM_2016: ThyroidGuideline = {
  title: 'ATA Guidelines for Diagnosis and Management of Hyperthyroidism (2016)',
  url: 'https://www.thyroid.org/professionals/ata-professional-guidelines/',
};

export const HYPOTHYROIDISM_ONGOING_MEASUREMENTS_URL =
  'https://liza.health/fhir/PlanDefinition/hypothyroidism-ongoing-measurements';
export const HYPERTHYROIDISM_ONGOING_MEASUREMENTS_URL =
  'https://liza.health/fhir/PlanDefinition/hyperthyroidism-ongoing-measurements';
/**
 * The two protocols version independently, despite sharing a file. They are separate
 * `PlanDefinition`s answering to different diagnoses and different guidelines, and the version is
 * carried in the canonical each expanded suggestion records as its provenance — so a shared constant
 * would restamp the untouched protocol's suggestions whenever the other one changed. A new ATA
 * edition correcting a hyperthyroidism analyte should leave hypothyroidism at the version it was
 * written against.
 */
export const HYPOTHYROIDISM_ONGOING_MEASUREMENTS_VERSION = '1.0.0';
export const HYPERTHYROIDISM_ONGOING_MEASUREMENTS_VERSION = '1.0.0';
/** Version-pinned canonicals, the values an expanded suggestion records as its provenance. */
export const HYPOTHYROIDISM_ONGOING_MEASUREMENTS_CANONICAL = `${HYPOTHYROIDISM_ONGOING_MEASUREMENTS_URL}|${HYPOTHYROIDISM_ONGOING_MEASUREMENTS_VERSION}`;
export const HYPERTHYROIDISM_ONGOING_MEASUREMENTS_CANONICAL = `${HYPERTHYROIDISM_ONGOING_MEASUREMENTS_URL}|${HYPERTHYROIDISM_ONGOING_MEASUREMENTS_VERSION}`;

/**
 * The thyroid analytes these protocols track.
 *
 * `TOTAL_T3` rather than free T3 is deliberate, and is the correction that matters most here: ATA
 * names *total* T3 as the hyperthyroidism monitoring analyte, because free T4 can normalise while
 * total T3 stays elevated. Free T3 is not the guideline's analyte and is not curated.
 */
export const THYROID_ONGOING_CODES = {
  TSH: LOINC_CODES.TSH,
  FREE_T4: LOINC_CODES.FREE_T4,
  TOTAL_T3: LOINC_CODES.TOTAL_T3,
} as const;

/**
 * The hypothyroidism codes this protocol answers to. `E03` is an ICD-10 *prefix* covering `E03.9`,
 * `E03.8` and the rest of the acquired-hypothyroidism category; the SNOMED code is matched exactly.
 */
export const HYPOTHYROIDISM_FOCUS_CODINGS = [
  { system: CODE_SYSTEMS.ICD10_CM, code: 'E03', display: 'Other hypothyroidism' },
  { system: CODE_SYSTEMS.SNOMED, code: '40930008', display: 'Hypothyroidism' },
] as const;

/**
 * The hyperthyroidism codes this protocol answers to. `E05` is an ICD-10 *prefix* covering Graves'
 * disease (`E05.0x`) and the rest of the thyrotoxicosis category.
 */
export const HYPERTHYROIDISM_FOCUS_CODINGS = [
  { system: CODE_SYSTEMS.ICD10_CM, code: 'E05', display: 'Thyrotoxicosis' },
  { system: CODE_SYSTEMS.SNOMED, code: '34486009', display: 'Hyperthyroidism' },
] as const;

/** Every action here is an unconditional patient-owned measurement; only code, cadence and words differ. */
const measurement = (
  source: ThyroidGuideline,
  id: string,
  title: string,
  description: string,
  code: string,
  timing: Timing,
  locus: string,
): PlanDefinitionAction => ({
  id,
  title,
  description,
  code: loincCode(code),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  timingTiming: timing,
  documentation: [{ type: 'citation', display: `${source.title}, ${locus}`, url: source.url }],
});

// --- hypothyroidism -----------------------------------------------------------------------------------

/**
 * AACE/ATA Recommendation 13 is a *sequence*, not a range: TSH at 4-8 weeks after a dose change, then
 * once at 6 months after the replacement dose is settled, then every 12 months. The planner has no way
 * to express a schedule that changes phase, so the steady state is what fires and the full wording is
 * kept beside it. Twelve months is the right single period — the earlier checks are consolidation
 * after a change, not an ongoing cadence.
 */
const hypothyroidTsh = measurement(
  AACE_ATA_HYPOTHYROIDISM_2012,
  'tsh',
  'TSH',
  'TSH yearly once the replacement dose is stable.',
  THYROID_ONGOING_CODES.TSH,
  every(1, 'a'),
  'Recommendation 13',
);

// --- hyperthyroidism ----------------------------------------------------------------------------------

/**
 * ATA monitors free T4 and total T3 every 2-3 months once a patient is euthyroid on a minimal
 * maintenance dose. The shorter end of that range is what fires.
 *
 * TSH is deliberately absent from this protocol. It stays suppressed for up to six months into
 * treatment, so ATA explicitly does not treat it as a valid early monitoring target — scheduling it
 * here would put a result on the plan that cannot be interpreted when it arrives.
 */
const hyperthyroidFreeT4 = measurement(
  ATA_HYPERTHYROIDISM_2016,
  'free-t4',
  'Free T4',
  'Free T4 every 2 months once euthyroid on a maintenance dose.',
  THYROID_ONGOING_CODES.FREE_T4,
  every(2, 'mo'),
  'antithyroid drug monitoring',
);

const hyperthyroidTotalT3 = measurement(
  ATA_HYPERTHYROIDISM_2016,
  'total-t3',
  'Total T3',
  'Total T3 every 2 months once euthyroid on a maintenance dose.',
  THYROID_ONGOING_CODES.TOTAL_T3,
  every(2, 'mo'),
  'antithyroid drug monitoring',
);

// --- the protocols ------------------------------------------------------------------------------------

const clinicalProtocolType = {
  coding: [
    {
      system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
      code: 'clinical-protocol',
      display: 'Clinical Protocol',
    },
  ],
};

/**
 * The measurement AACE/ATA asks for on an ongoing basis in treated hypothyroidism.
 *
 * Free T4 is deliberately absent. AACE/ATA says it "should be considered" when monitoring
 * replacement, and downgrades it on the grounds that it should only be used selectively; it is
 * genuinely required in central hypothyroidism, where TSH is unreliable, which is a different
 * diagnosis from the one this protocol matches.
 */
export const hypothyroidismOngoingMeasurements: PlanDefinition = {
  resourceType: 'PlanDefinition',
  url: HYPOTHYROIDISM_ONGOING_MEASUREMENTS_URL,
  version: HYPOTHYROIDISM_ONGOING_MEASUREMENTS_VERSION,
  identifier: [{ system: 'urn:ietf:rfc:3986', value: HYPOTHYROIDISM_ONGOING_MEASUREMENTS_URL }],
  name: 'hypothyroidism-ongoing-measurements',
  title: 'Hypothyroidism — ongoing measurements',
  status: 'active',
  experimental: true,
  type: clinicalProtocolType,
  date: '2026-09-15',
  publisher: 'Liza Health',
  description:
    'Results recommended for ongoing monitoring in hypothyroidism, expanded onto a care-plan topic when a hypothyroidism Condition is linked to it.',
  useContext: [
    {
      code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'focus' },
      valueCodeableConcept: { coding: [...HYPOTHYROIDISM_FOCUS_CODINGS] },
    },
  ],
  relatedArtifact: [
    { type: 'derived-from', display: AACE_ATA_HYPOTHYROIDISM_2012.title, url: AACE_ATA_HYPOTHYROIDISM_2012.url },
  ],
  action: [
    {
      id: 'ongoing-measurements',
      title: 'Ongoing measurements',
      description: 'Results tracked on an ongoing basis for hypothyroidism.',
      action: [hypothyroidTsh],
    },
  ],
};

/**
 * The measurements ATA asks for on an ongoing basis in treated hyperthyroidism.
 *
 * Note the analyte pair: free T4 and *total* T3, with TSH absent. This is the opposite emphasis from
 * hypothyroidism, where TSH is the whole protocol, and it is what the guideline asks for rather than
 * an oversight.
 */
export const hyperthyroidismOngoingMeasurements: PlanDefinition = {
  resourceType: 'PlanDefinition',
  url: HYPERTHYROIDISM_ONGOING_MEASUREMENTS_URL,
  version: HYPERTHYROIDISM_ONGOING_MEASUREMENTS_VERSION,
  identifier: [{ system: 'urn:ietf:rfc:3986', value: HYPERTHYROIDISM_ONGOING_MEASUREMENTS_URL }],
  name: 'hyperthyroidism-ongoing-measurements',
  title: 'Hyperthyroidism — ongoing measurements',
  status: 'active',
  experimental: true,
  type: clinicalProtocolType,
  date: '2026-09-15',
  publisher: 'Liza Health',
  description:
    'Results recommended for ongoing monitoring in hyperthyroidism, expanded onto a care-plan topic when a hyperthyroidism Condition is linked to it.',
  useContext: [
    {
      code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'focus' },
      valueCodeableConcept: { coding: [...HYPERTHYROIDISM_FOCUS_CODINGS] },
    },
  ],
  relatedArtifact: [
    { type: 'derived-from', display: ATA_HYPERTHYROIDISM_2016.title, url: ATA_HYPERTHYROIDISM_2016.url },
  ],
  action: [
    {
      id: 'ongoing-measurements',
      title: 'Ongoing measurements',
      description: 'Results tracked on an ongoing basis for hyperthyroidism.',
      action: [hyperthyroidFreeT4, hyperthyroidTotalT3],
    },
  ],
};
